import { env } from '@/lib/env';
import { clearAccessToken, getAccessToken, setAccessToken } from '@/services/authTokenStorage';
import { accessTokenIdentity } from '@/services/authSessionClaims';
import { getSessionOriginKey, isCurrentSessionOrigin } from '@/services/sessionOrigin';

/**
 * Silent access-token refresh for the S02 JWT session.
 *
 * Production Construction OS is not MSAL / Entra-on-the-browser. Staff sessions
 * are an access JWT in localStorage plus an httpOnly `helios_refresh_token`
 * cookie scoped to `/api/v1/auth`. This module is the single place that posts
 * `/auth/refresh` so concurrent 401s share one rotation.
 */

export interface RefreshedAuthTokens {
  authenticated: true;
  accessToken: string;
  accessTokenExpiresAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    title: string | null;
    tenantId: string;
    organizationId: string | null;
    officeId: string | null;
    version?: number;
  };
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  roles: string[];
  permissions: string[];
  sessionId?: string;
  authVersion?: number;
  accessMode?: 'FULL' | 'PASSWORD_CHANGE_REQUIRED';
  temporaryPasswordExpiresAt?: string | null;
}

export interface SilentRefreshResult {
  ok: boolean;
  data: RefreshedAuthTokens | null;
}

type SessionEndedListener = () => void;

let inFlight: Promise<SilentRefreshResult> | null = null;
let refreshAbort: AbortController | null = null;
let sessionGeneration = 0;
const sessionEndedListeners = new Set<SessionEndedListener>();

const trimEnd = (value: string): string => value.replace(/\/+$/, '');

export function resolveAuthRefreshUrl(apiBaseUrl: string = env.apiBaseUrl): string {
  const normalizedBase = trimEnd(apiBaseUrl);
  if (normalizedBase.endsWith('/auth/refresh')) {
    return normalizedBase;
  }
  return `${normalizedBase}/auth/refresh`;
}

export function isSilentRefreshEligible(path: string): boolean {
  const relative = toAuthRelativePath(path);
  if (relative === '/auth/refresh' || relative.startsWith('/auth/refresh/')) {
    return false;
  }
  if (
    relative === '/auth/login' ||
    relative === '/auth/demo' ||
    relative === '/auth/demo-access' ||
    relative === '/auth/environments' ||
    relative === '/auth/logout'
  ) {
    return false;
  }
  if (relative.startsWith('/auth/passkeys/authentication/')) {
    return false;
  }
  return true;
}

export function onAccessTokenRefreshFailed(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

export function abandonSilentRefresh(): void {
  sessionGeneration += 1;
  refreshAbort?.abort();
}

export async function settleSilentRefresh(): Promise<void> {
  if (!inFlight) {
    return;
  }
  try {
    await inFlight;
  } catch {
    // Logout waits only so a late Set-Cookie can be revoked next.
  }
}

export function resetSessionRefreshForTests(): void {
  refreshAbort?.abort();
  refreshAbort = null;
  inFlight = null;
  sessionGeneration = 0;
  sessionEndedListeners.clear();
}

export async function refreshAccessTokenOnce(sessionOrigin?: string | null): Promise<SilentRefreshResult> {
  if (sessionOrigin !== undefined && !isCurrentSessionOrigin(sessionOrigin)) {
    return { ok: false, data: null };
  }
  if (inFlight) {
    const result = await inFlight;
    return sessionOrigin !== undefined && !isCurrentSessionOrigin(sessionOrigin)
      ? { ok: false, data: null }
      : result;
  }

  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });
  const result = await inFlight;
  return sessionOrigin !== undefined && !isCurrentSessionOrigin(sessionOrigin)
    ? { ok: false, data: null }
    : result;
}

async function performRefresh(): Promise<SilentRefreshResult> {
  const generation = sessionGeneration;
  const startingToken = getAccessToken();
  const startingIdentity = accessTokenIdentity(startingToken);
  const startingOrigin = getSessionOriginKey();
  const originUnchanged = () => generation === sessionGeneration && (
    startingOrigin !== null
      ? isCurrentSessionOrigin(startingOrigin)
      : getAccessToken() === startingToken
  );
  const abort = new AbortController();
  refreshAbort = abort;
  try {
    const response = await fetch(resolveAuthRefreshUrl(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      signal: abort.signal,
    });

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    const tokens = readRefreshedTokens(body);
    if (!originUnchanged()) {
      return { ok: false, data: null };
    }
    if (!response.ok || !tokens) {
      clearAccessToken();
      notifySessionEnded();
      return { ok: false, data: null };
    }

    if (startingIdentity !== null && accessTokenIdentity(tokens.accessToken) !== startingIdentity) {
      return { ok: false, data: null };
    }

    setAccessToken(tokens.accessToken);
    return { ok: true, data: tokens };
  } catch {
    if (!originUnchanged()) {
      return { ok: false, data: null };
    }
    clearAccessToken();
    notifySessionEnded();
    return { ok: false, data: null };
  } finally {
    if (refreshAbort === abort) {
      refreshAbort = null;
    }
  }
}

function notifySessionEnded(): void {
  for (const listener of sessionEndedListeners) {
    listener();
  }
}

function readRefreshedTokens(body: unknown): RefreshedAuthTokens | null {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const record = body as Record<string, unknown>;
  const payload = 'success' in record ? (record.success ? record.data : null) : body;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const accessToken = (payload as { accessToken?: unknown }).accessToken;
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    return null;
  }

  return payload as RefreshedAuthTokens;
}

function toAuthRelativePath(path: string): string {
  const pathname = toPathname(path);
  const stripped = pathname.replace(/^\/api\/v1(?=\/|$)/, '');
  return stripped.length > 0 ? stripped : '/';
}

function toPathname(path: string): string {
  const raw = path.trim();
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw.split('?')[0] ?? raw;
    }
  }
  const withoutQuery = raw.split('?')[0] ?? raw;
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}
